// THE PAGE SIDEBAR — its side, the person's hidden choice, and the one toggle
// (Nika 2026-10-29: "for apps, studio, tasks, and all other pages that open in
// shell, move sidebar at the right - if navbar is at the left, and opposite.
// for top and bottom - keep them. also restyle and fix sidebar styles, make
// them consistent and make them toggleable"; the toggle pick: "Hide fully,
// remember per page" — one button at the same place on every page hides the
// sidebar, a small tab at the page edge brings it back, the choice is kept per
// page with the navbar choice, and the PageSlot window's sidebar icon is the
// same toggle).
//
// Every page's own sidebar is the shared AppSidebar (components/AppSidebar.js).
// It reads its posture here and nowhere else:
//
//   el.call('pageSidebarRead', s.root, el)
//     → { key, side: 'left' | 'right', hidden, phone, toggleable, follows }
//   el.call('pageSidebarSet', key, true | false)
//   el.call('pageSidebarToggle')            the calling element's page
//
// SIDE — `navbarLayoutRead(root).sidebarSide` (functions/navbarLayout.js): the
// side the navbar does not take. A phone keeps the shell's phone posture (the
// sidebar reads 'left' and never hidden; MobileRailSheet is the phone rail).
//
// THE OPT-IN (`follows`) — a page moves its sidebar onto the rule by setting
// `pageSidebar: true` on its AppSidebar (one page at a time, so a page whose
// own layout has not been checked on the other side never moves by
// accident). A sidebar without it — and every element inside it — reads
// 'left', as before, and shows no hide button; its hidden choice still holds
// (the PageSlot window's icon reaches every page). Page chrome OUTSIDE the
// sidebar that asks (an island that sits opposite the sidebar) asks on its
// page's behalf: it follows the rule. The PageSlot icon asks with the page's
// own sidebar, so it reads that sidebar's answer.
//
// KEY — one per app page (pageSidebarKey): the page at its own URL and the
// same page in a PageSlot window share it, so the window's icon and the
// page's own toggle always agree.
//
// MEMORY — the person's look (`user_preferences.homeLook.sidebarHidden`, a map
// of page key → boolean), through the look's one writer, homeLookSet (workspace
// functions/homeLook.js): this session at once, this device, the record — the
// same bag and the same precedence as the navbar's place. A host without the
// look (the canvas or the preview app alone) cannot remember, so it offers no
// toggle (`toggleable: false`) and its sidebars always show.
//
// FRANK: every export is self-contained (FA204) — the key rule is restated
// where it is used; siblings are reached through `this.call`.

// The page identity of a route — the first path segment once the workspace
// prefix and the query are gone: '/w/acme/tasks/feed?x=1' → 'tasks',
// '/apps' → 'apps', '/' → 'home'. Bounded by the apps, never by a record id
// (/task/<id> → 'task'), so the stored map stays small.
export const pageSidebarKey = function pageSidebarKey(path) {
  const raw = String(path || '').split(/[?#]/)[0]
  const fns = this && this.context && this.context.functions
  const stripped =
    fns && typeof fns.stripWorkspacePrefix === 'function'
      ? this.call('stripWorkspacePrefix', raw)
      : raw.replace(/^\/w\/[a-z0-9-]{1,48}(?=\/|$)/, '')
  const first = String(stripped || '')
    .split('/')
    .filter(Boolean)[0]
  const key = String(first || 'home').toLowerCase()
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(key) ? key : 'home'
}

// The posture of the page sidebar the element `at` belongs to (the AppSidebar
// itself, or any control inside the same page). Inside a PageSlot pane the
// page is the pane's route; anywhere else it is the address.
//
// Reactive: a factory that calls this subscribes through what it reads — the
// look (`_homeLook`, `userPrefs`), the phone flag, the address and both pane
// keys. Every key is read on every run, before any decision (a factory that
// returns before it reads a key never re-runs on that key).
export const pageSidebarRead = function pageSidebarRead(root, at) {
  const r = root && typeof root === 'object' ? root : {}
  const self = this && typeof this.call === 'function' ? this : null
  const fns = (self && self.context && self.context.functions) || {}
  const address = r.currentPath
  const panes = { _canvasSlot: r._canvasSlot, _canvasSlotSecond: r._canvasSlotSecond }
  const nav =
    (self && typeof fns.navbarLayoutRead === 'function' && self.call('navbarLayoutRead', r)) || {}
  const look =
    (self && typeof fns.homeLookRead === 'function' && self.call('homeLookRead', r)) || {}
  const pane =
    self && typeof fns.slotPaneKeyForStrict === 'function'
      ? self.call('slotPaneKeyForStrict', at || self)
      : null
  const path = pane ? panes[pane] : address
  const key = self ? self.call('pageSidebarKey', path) : 'home'
  const map = look.sidebarHidden && typeof look.sidebarHidden === 'object' ? look.sidebarHidden : {}
  const phone = nav.phone === true
  const toggleable = !phone && typeof fns.homeLookSet === 'function'
  // THE OPT-IN: the sidebar `at` is or sits in (AppSidebar's own mark, a few
  // steps up — the hide button, the reopen tab and its glyph) follows the
  // rule only with `pageSidebar: true`; outside any sidebar, the caller asks
  // for its page and follows.
  let bar = null
  let step = at && typeof at === 'object' ? at : null
  for (let hops = 0; step && hops < 4 && !bar; hops++) {
    if (step.attr && Object.prototype.hasOwnProperty.call(step.attr, 'data-app-sidebar')) bar = step
    step = step.parent
  }
  const follows = bar ? bar.pageSidebar === true : true
  return {
    key,
    side: follows && !phone && nav.sidebarSide === 'right' ? 'right' : 'left',
    hidden: toggleable && map[key] === true,
    phone,
    toggleable,
    follows
  }
}

// A decision: `el.call('pageSidebarSet', 'tasks', true)` — through the look's
// one writer, so the session answers at once and the record keeps it (one
// per-path write per page: a second tab's choice for another page survives).
// Returns the write's promise; null where the host cannot remember.
export const pageSidebarSet = function pageSidebarSet(key, hidden) {
  const self = this && typeof this.call === 'function' ? this : null
  const k = String(key || '')
  const fns = (self && self.context && self.context.functions) || {}
  if (!self || typeof fns.homeLookSet !== 'function' || !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(k)) {
    return Promise.resolve(null)
  }
  return Promise.resolve(self.call('homeLookSet', { sidebarHidden: { [k]: hidden === true } }))
}

// The one toggle — the calling element's page: shown → hidden, hidden → shown.
// The AppSidebar's own button, its reopen tab and the PageSlot window's
// sidebar icon all call this (or pageSidebarSet with the same key).
export const pageSidebarToggle = function pageSidebarToggle() {
  const self = this && typeof this.call === 'function' ? this : null
  if (!self) return Promise.resolve(null)
  const now = self.call('pageSidebarRead', self.state && self.state.root, self) || {}
  if (!now.toggleable) return Promise.resolve(null)
  return self.call('pageSidebarSet', now.key, !now.hidden)
}
